import { useState, useEffect } from 'react';
import { Search, Filter, CheckCircle, Truck, Clock, Package, Mail, FileText, QrCode, Copy, Send, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

// POS-grade order statuses
const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'created', label: 'Created' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready_for_collection', label: 'Ready for Collection' },
  { value: 'collected', label: 'Collected' },
  { value: 'cancelled', label: 'Cancelled' },
  // Legacy
  { value: 'pending', label: 'Pending (Legacy)' },
  { value: 'ready', label: 'Ready (Legacy)' },
  { value: 'delivered', label: 'Delivered (Legacy)' }
];

export default function AdminOrders() {
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [collectionCode, setCollectionCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [quickCode, setQuickCode] = useState('');
  const [quickLooking, setQuickLooking] = useState(false);
  const [quickOrder, setQuickOrder] = useState(null);
  const [quickVerifying, setQuickVerifying] = useState(false);
  const [overrideOrder, setOverrideOrder] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideRef, setOverrideRef] = useState('');
  const [overriding, setOverriding] = useState(false);

  const isFinance = user?.role === 'finance';

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 30 seconds for live BOH sync
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [token, statusFilter]);

  const fetchOrders = async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/admin/orders`
        : `${API_URL}/api/admin/orders?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update status');
      }

      if (newStatus === 'ready_for_collection' && data.collection_code) {
        toast.success(`Order ready! Collection code: ${data.collection_code}`);
      } else {
        toast.success('Order status updated');
      }
      
      fetchOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const verifyCollection = async (orderId) => {
    if (!collectionCode.trim()) {
      toast.error('Please enter the collection code');
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/verify-collection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ collection_code: collectionCode })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to verify collection');
      }

      toast.success('Collection verified successfully!');
      setCollectionCode('');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setVerifying(false);
    }
  };

  const sendCollectionEmail = async (orderId) => {
    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/send-collection-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
      }
      
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to send email');
      }

      toast.success('Collection email sent to customer');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadInvoice = (orderId) => {
    window.open(`${API_URL}/api/orders/${orderId}/invoice?token=${token}`, '_blank');
  };

  const lookupByCode = async () => {
    const code = quickCode.trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a collection code');
      return;
    }
    setQuickLooking(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/lookup-by-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ collection_code: code })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Order not found');
      }
      setQuickOrder(data);
    } catch (error) {
      toast.error(error.message);
      setQuickOrder(null);
    } finally {
      setQuickLooking(false);
    }
  };

  const confirmQuickVerify = async () => {
    if (!quickOrder) return;
    setQuickVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${quickOrder.order_id}/verify-collection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ collection_code: quickOrder.collection_code })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      toast.success(`Order collected! ${quickOrder.customer_name}'s order is complete.`);
      setQuickOrder(null);
      setQuickCode('');
      fetchOrders();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setQuickVerifying(false);
    }
  };

  const handlePaymentOverride = async () => {
    if (!overrideOrder || !overrideReason.trim() || !overrideRef.trim()) return;
    setOverriding(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/orders/${overrideOrder.order_id}/payment-override`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: overrideReason, payment_reference: overrideRef })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Override failed');
      toast.success(`Order marked as paid! Code: ${data.collection_code}`);
      setOverrideOrder(null);
      setOverrideReason('');
      setOverrideRef('');
      fetchOrders();
    } catch (e) { toast.error(e.message); }
    finally { setOverriding(false); }
  };

  const deleteOrder = async (orderId, isPaid) => {
    const confirmMsg = isPaid 
      ? 'This is a cancelled order. Are you sure you want to delete it permanently?'
      : 'Are you sure you want to delete this unpaid order? This cannot be undone.';
    
    if (!confirm(confirmMsg)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete order');
      }

      toast.success('Order deleted successfully');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyCollectionCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Collection code copied!');
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      order.order_id.toLowerCase().includes(q) ||
      (order.customer_name || '').toLowerCase().includes(q) ||
      (order.customer_email || '').toLowerCase().includes(q) ||
      (order.customer_phone || '').toLowerCase().includes(q) ||
      (order.collection_code || '').toLowerCase().includes(q) ||
      (order.delivery_date || '').includes(q) ||
      (order.delivery_address_name || '').toLowerCase().includes(q) ||
      (order.items || []).some(item => (item.name || '').toLowerCase().includes(q))
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      created: 'bg-gray-100 text-gray-800',
      payment_pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      preparing: 'bg-orange-100 text-orange-800',
      ready_for_collection: 'bg-green-100 text-green-800',
      collected: 'bg-emerald-500 text-white',
      cancelled: 'bg-red-100 text-red-800',
      // Legacy
      pending: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-green-100 text-green-800',
      delivered: 'bg-emerald-500 text-white'
    };
    const label = status.replace(/_/g, ' ').toUpperCase();
    return <Badge className={styles[status] || 'bg-gray-100'}>{label}</Badge>;
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-semibold text-secondary-900">Orders</h1>
        <p className="text-muted-foreground">Manage and track all customer orders</p>
      </div>

      {/* Quick Verify by Collection Code */}
      {!isFinance && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-secondary-900">Quick Verify Collection</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Enter a collection code to instantly find and verify the order</p>
            <div className="flex gap-2">
              <Input
                value={quickCode}
                onChange={(e) => setQuickCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-character code"
                className="font-mono text-lg tracking-wider flex-1 bg-white"
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && lookupByCode()}
                data-testid="quick-verify-input"
              />
              <Button
                onClick={lookupByCode}
                disabled={quickLooking || !quickCode.trim()}
                className="bg-primary hover:bg-primary/90 min-w-[120px]"
                data-testid="quick-verify-lookup-btn"
              >
                {quickLooking ? 'Looking...' : 'Find Order'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Verify Confirmation Dialog */}
      <Dialog open={!!quickOrder} onOpenChange={(open) => { if (!open) { setQuickOrder(null); } }}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-600" />
              Confirm Collection
            </DialogTitle>
          </DialogHeader>
          {quickOrder && (
            <div className="space-y-4 pt-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-sm text-green-600 font-medium">Collection Code</p>
                <p className="font-mono text-2xl font-bold text-green-800 tracking-wider">{quickOrder.collection_code}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Customer</p>
                  <p className="font-semibold text-sm">{quickOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(quickOrder.status)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Delivery Date</p>
                  <p className="text-sm">{quickOrder.delivery_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-bold text-primary text-sm">R{quickOrder.total_amount?.toFixed(2)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-2">Order Items</p>
                <div className="bg-slate-50 rounded-lg divide-y divide-slate-200">
                  {quickOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">{item.name} x{item.quantity}</p>
                        {item.selected_sides?.length > 0 && (
                          <p className="text-xs text-green-700">Sides: {item.selected_sides.map(s => s.name).join(', ')}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold">R{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {quickOrder.special_instructions && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-600 font-medium">Special Instructions</p>
                  <p className="text-sm">{quickOrder.special_instructions}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setQuickOrder(null); }} data-testid="quick-verify-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={confirmQuickVerify}
              disabled={quickVerifying}
              className="bg-green-600 hover:bg-green-700"
              data-testid="quick-verify-confirm-btn"
            >
              {quickVerifying ? 'Verifying...' : 'Confirm & Complete Collection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-orders"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-56" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <Card key={order.order_id} className="hover:border-primary/30 transition-colors" data-testid={`order-${order.order_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Order Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <p className="font-mono text-sm text-muted-foreground">{order.order_id}</p>
                      {getStatusBadge(order.status)}
                      {order.payment_status !== 'paid' && (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">Unpaid</Badge>
                      )}
                    </div>
                    <p className="font-semibold text-secondary-900 text-lg">{order.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                      <span>{order.items.length} items</span>
                      <span>•</span>
                      <span className="font-semibold text-secondary-900">R{order.total_amount.toFixed(2)}</span>
                      <span>•</span>
                      <span>Delivery: {order.delivery_date}</span>
                    </div>
                    
                    {/* Show items with sides in card view */}
                    {order.items.some(i => i.selected_sides?.length > 0) && (
                      <div className="mt-2 space-y-1">
                        {order.items.map((item) => (
                          item.selected_sides?.length > 0 && (
                            <p key={`${order.order_id}-${item.item_id}`} className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded inline-flex items-center gap-1 mr-1">
                              {item.name}: {item.selected_sides.map(s => s.name).join(', ')}
                            </p>
                          )
                        ))}
                      </div>
                    )}
                    
                    {/* Collection Code Display */}
                    {order.collection_code && order.status === 'ready_for_collection' && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg inline-flex items-center gap-3">
                        <QrCode className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-xs text-green-600 font-medium">Collection Code</p>
                          <p className="font-mono text-xl font-bold text-green-800 tracking-wider">{order.collection_code}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCollectionCode(order.collection_code)}
                          className="ml-2"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {/* Status transition buttons - only for non-finance roles */}
                    {!isFinance && (
                      <>
                        {order.status === 'paid' && (
                          <Button
                            size="sm"
                            onClick={() => updateOrderStatus(order.order_id, 'preparing')}
                            className="bg-orange-500 hover:bg-orange-600"
                            data-testid={`start-preparing-${order.order_id}`}
                          >
                            <Package className="h-4 w-4 mr-1" />
                            Start Preparing
                          </Button>
                        )}
                        {order.status === 'preparing' && (
                          <Button
                            size="sm"
                            onClick={() => updateOrderStatus(order.order_id, 'ready_for_collection')}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`mark-ready-${order.order_id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Ready for Collection
                          </Button>
                        )}
                        {order.status === 'ready_for_collection' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendCollectionEmail(order.order_id)}
                              disabled={sendingEmail}
                              data-testid={`send-email-${order.order_id}`}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              {sendingEmail ? 'Sending...' : 'Send Email'}
                            </Button>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  className="bg-primary hover:bg-primary-600"
                                  onClick={() => setSelectedOrder(order)}
                                  data-testid={`verify-collection-${order.order_id}`}
                                >
                                  <Truck className="h-4 w-4 mr-1" />
                                  Verify Collection
                                </Button>
                              </DialogTrigger>
                              <DialogContent aria-describedby={undefined}>
                                <DialogHeader>
                                  <DialogTitle>Verify Collection</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                  <p className="text-muted-foreground">
                                    Enter the collection code provided by <strong>{order.customer_name}</strong> to verify pickup.
                                  </p>
                                  <div>
                                    <label className="text-sm font-medium">Collection Code</label>
                                    <Input
                                      value={collectionCode}
                                      onChange={(e) => setCollectionCode(e.target.value.toUpperCase())}
                                      placeholder="Enter 6-character code"
                                      className="font-mono text-lg tracking-wider mt-1"
                                      maxLength={6}
                                      data-testid="collection-code-input"
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Expected code: <span className="font-mono font-bold">{order.collection_code}</span>
                                  </p>
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => {
                                    setSelectedOrder(null);
                                    setCollectionCode('');
                                  }}>
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={() => verifyCollection(order.order_id)}
                                    disabled={verifying}
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid="verify-collection-btn"
                                  >
                                    {verifying ? 'Verifying...' : 'Verify & Complete'}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </>
                        )}
                      </>
                    )}
                    
                    {/* Invoice download - available to all */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadInvoice(order.order_id)}
                      data-testid={`download-invoice-${order.order_id}`}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Invoice
                    </Button>
                    
                    {/* View details */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`view-details-${order.order_id}`}>
                          View Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg" aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>Order Details</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Order ID</p>
                              <p className="font-mono">{order.order_id}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Status</p>
                              {getStatusBadge(order.status)}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Customer</p>
                            <p>{order.customer_name}</p>
                            <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                            {order.customer_phone && <p className="text-sm text-muted-foreground">{order.customer_phone}</p>}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Delivery Date</p>
                              <p>{order.delivery_date}</p>
                            </div>
                            {order.delivery_time && (
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Delivery Time</p>
                                <p>{order.delivery_time}</p>
                              </div>
                            )}
                          </div>
                          {order.collection_code && (
                            <div className="p-3 bg-primary/10 rounded-lg">
                              <p className="text-sm font-medium text-muted-foreground">Collection Code</p>
                              <p className="font-mono text-2xl font-bold text-primary tracking-wider">{order.collection_code}</p>
                              {order.collection_code_used && (
                                <p className="text-xs text-green-600 mt-1">Used on {order.collected_at}</p>
                              )}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-2">Items</p>
                            <div className="space-y-3">
                              {order.items.map((item) => (
                                <div key={`detail-${order.order_id}-${item.item_id || item.name}`} className="py-2 border-b last:border-0">
                                  <div className="flex justify-between text-sm">
                                    <span className="font-medium">{item.name} × {item.quantity}</span>
                                    <span className="font-semibold">R{(item.price * item.quantity).toFixed(2)}</span>
                                  </div>
                                  {item.selected_sides?.length > 0 && (
                                    <p className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded mt-1">
                                      Side: {item.selected_sides.map(s => s.name).join(', ')}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          {order.special_instructions && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Special Instructions</p>
                              <p className="text-sm bg-gray-50 p-2 rounded">{order.special_instructions}</p>
                            </div>
                          )}
                          <div className="border-t pt-4">
                            <div className="flex justify-between font-bold text-lg">
                              <span>Total</span>
                              <span className="text-primary">R{order.total_amount.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Payment Override - for pending/created orders, admin only */}
                    {(order.status === 'created' || order.status === 'payment_pending' || order.status === 'pending') && 
                     order.payment_status !== 'paid' && !isFinance &&
                     (user?.role === 'super_admin' || user?.role === 'manager') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => setOverrideOrder(order)}
                        data-testid={`override-${order.order_id}`}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Mark Paid
                      </Button>
                    )}
                    
                    {/* Delete button - only for unpaid or cancelled orders */}
                    {(order.payment_status !== 'paid' || order.status === 'cancelled') && !isFinance && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => deleteOrder(order.order_id, order.payment_status === 'paid')}
                        data-testid={`delete-order-${order.order_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No orders found</p>
          </CardContent>
        </Card>
      )}

      {/* Payment Override Dialog */}
      <Dialog open={!!overrideOrder} onOpenChange={(open) => { if (!open) { setOverrideOrder(null); setOverrideReason(''); setOverrideRef(''); } }}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Override Payment
            </DialogTitle>
          </DialogHeader>
          {overrideOrder && (
            <div className="space-y-4 pt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Order #{overrideOrder.order_id?.slice(-8).toUpperCase()}</strong> for <strong>{overrideOrder.customer_name}</strong>
                </p>
                <p className="text-xs text-amber-700 mt-1">Total: R{overrideOrder.total_amount?.toFixed(2)}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Reason for override *</label>
                <textarea
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                  placeholder="e.g. Customer paid via EFT, cash received at counter..."
                  rows={2}
                  className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 outline-none"
                  data-testid="override-reason"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Payment reference *</label>
                <Input
                  value={overrideRef}
                  onChange={e => setOverrideRef(e.target.value)}
                  placeholder="e.g. EFT ref, receipt number, cash register ID..."
                  data-testid="override-ref"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setOverrideOrder(null); setOverrideReason(''); setOverrideRef(''); }}>Cancel</Button>
            <Button onClick={handlePaymentOverride} disabled={overriding || !overrideReason.trim() || !overrideRef.trim()}
              className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-override">
              {overriding ? 'Processing...' : 'Confirm Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
